import { forwardRef, useState } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { useTheme } from '@elka/theme';
import { Spinner } from './Spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'gradient' | 'outline' | 'success';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  /**
   * Enable responsive mobile styling.
   * When true, button automatically adapts for mobile:
   * - Larger touch targets (44px min height on mobile)
   * - Full width on mobile, auto on desktop
   * - Larger text and padding on mobile
   * - Visible border on mobile for better visibility
   */
  responsive?: boolean;
  /**
   * Mobile-specific size override.
   * Only applies when responsive is true.
   * Defaults to one size larger than the desktop size.
   */
  mobileSize?: 'sm' | 'md' | 'lg';
  /**
   * Calm-v2 styling: flat solid brand, radius 8, no shadow, no scale,
   * color-only transitions. `gradient`/`outline` variants map to
   * `primary`/`secondary`. Only valid in apps that import
   * `@elka/theme` v2/calm.css. Default (false) keeps legacy styling.
   */
  calm?: boolean;
}

// Calm-v2 variant classes (token utilities from @elka/theme v2/calm.css).
// `gradient`/`outline` are not keyed here — no caller pairs `calm` with either
// variant, and the `CALM_VARIANTS[variant] || CALM_VARIANTS.primary` fallback
// below covers them.
const CALM_VARIANTS: Partial<Record<NonNullable<ButtonProps['variant']>, string>> = {
  primary: 'bg-brand text-white hover:bg-brand-ink',
  secondary:
    'bg-surface text-ink-2 border border-line hover:border-line-strong hover:bg-surface-soft',
  ghost: 'bg-transparent text-muted hover:text-ink hover:bg-surface-soft',
  danger: 'bg-danger text-white hover:opacity-90',
  success: 'bg-success text-white hover:opacity-90',
};

const CALM_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-2.5 py-1 text-xs min-h-[28px]',
  md: 'px-[13px] py-[7px] text-[13px] min-h-[34px]',
  lg: 'px-4 py-2 text-sm min-h-[40px]',
};

// Brand colors for each theme (matching Tailwind colors used in @elka/theme)
const BRAND_COLORS = {
  elkaBlue: {
    primary: '#0C1A55',
    secondary: '#3C5FA8',
    accent: '#E9F0FF',
    light: '#F4F6FA',
    // Gradient: from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]
    gradientStart: '#0C1A55',
    gradientMid: '#3C5FA8',
    gradientEnd: '#0C1A55',
    // Hover gradient: from-[#0A1545] via-[#2F4A8A] to-[#0A1545]
    gradientHoverStart: '#0A1545',
    gradientHoverMid: '#2F4A8A',
    gradientHoverEnd: '#0A1545',
  },
  arabGolden: {
    primary: '#b45309', // amber-700 (hover)
    secondary: '#d97706', // amber-600 (primary button)
    accent: '#fef3c7', // amber-100 (secondary bg)
    light: '#fde68a', // amber-200 (secondary hover)
    // Gradient: from-amber-500 via-yellow-500 to-orange-500
    gradientStart: '#f59e0b', // amber-500
    gradientMid: '#eab308', // yellow-500
    gradientEnd: '#f97316', // orange-500
    // Hover gradient: from-amber-600 via-yellow-600 to-orange-600
    gradientHoverStart: '#d97706', // amber-600
    gradientHoverMid: '#ca8a04', // yellow-600
    gradientHoverEnd: '#ea580c', // orange-600
  },
};

// Get variant styles based on theme
const getVariantStyles = (themeName: 'elkaBlue' | 'arabGolden') => {
  const BRAND = BRAND_COLORS[themeName];
  // For secondary text color in arabGolden, use amber-900
  const secondaryTextColor = themeName === 'arabGolden' ? '#78350f' : BRAND.primary;

  return {
    primary: {
      base: { backgroundColor: BRAND.secondary, color: 'white' },
      hover: { backgroundColor: BRAND.primary },
      className: 'text-white',
    },
    secondary: {
      base: { backgroundColor: BRAND.accent, color: secondaryTextColor },
      hover: { backgroundColor: BRAND.light },
      className: '',
    },
    danger: {
      base: { backgroundColor: '#dc2626', color: 'white' },
      hover: { backgroundColor: '#b91c1c' },
      className: 'text-white focus:ring-red-500',
    },
    ghost: {
      base: { backgroundColor: 'transparent', color: '#374151' },
      hover: { backgroundColor: BRAND.accent },
      className: 'shadow-none',
    },
    gradient: {
      base: {
        background: `linear-gradient(to right, ${BRAND.gradientStart}, ${BRAND.gradientMid}, ${BRAND.gradientEnd})`,
        color: 'white',
      },
      hover: {
        background: `linear-gradient(to right, ${BRAND.gradientHoverStart}, ${BRAND.gradientHoverMid}, ${BRAND.gradientHoverEnd})`,
      },
      className: 'text-white shadow-lg',
    },
    outline: {
      base: {
        backgroundColor: 'white',
        color: BRAND.secondary,
        border: `2px solid ${BRAND.secondary}`,
      },
      hover: { backgroundColor: BRAND.accent },
      className: '',
    },
    success: {
      base: { backgroundColor: '#22c55e', color: 'white' },
      hover: { backgroundColor: '#22c55e' }, // No hover change for success state
      className: 'text-white',
    },
  };
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      responsive = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- accepted for forward-compat; not wired yet
      mobileSize,
      calm = false,
      disabled,
      style,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Get current theme
    const { theme } = useTheme();
    const BRAND = BRAND_COLORS[theme];
    const variantStyles = getVariantStyles(theme);

    const baseClasses =
      'inline-flex items-center justify-center rounded-xl font-medium transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-95';

    // Desktop sizes (or non-responsive)
    const sizes = {
      sm: 'px-4 py-2 text-sm min-h-[36px]',
      md: 'px-6 py-2.5 text-base min-h-[44px]',
      lg: 'px-8 py-3 text-lg min-h-[48px]',
    };

    // Responsive sizes: mobile-first with desktop overrides
    // Mobile: larger touch targets, full width, visible border
    // Desktop: compact sizing, auto width
    const responsiveSizes = {
      sm: 'px-4 py-2.5 text-base min-h-[44px] md:px-3 md:py-1.5 md:text-sm md:min-h-0',
      md: 'px-5 py-3 text-base min-h-[48px] md:px-4 md:py-2 md:text-sm md:min-h-[36px]',
      lg: 'px-6 py-3.5 text-lg min-h-[52px] md:px-6 md:py-2.5 md:text-base md:min-h-[44px]',
    };

    // Get the appropriate size classes
    const sizeClasses = responsive ? responsiveSizes[size] : sizes[size];

    // Responsive classes for width and border
    const responsiveClasses = responsive
      ? 'w-full md:w-auto border border-gray-200 md:border-0'
      : '';

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    if (calm) {
      return (
        <button
          ref={ref}
          className={clsx(
            // No focus:outline-none here — calm.css's :focus-visible brand ring must apply
            'rounded-control inline-flex items-center justify-center font-bold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
            CALM_VARIANTS[variant] || CALM_VARIANTS.primary,
            CALM_SIZES[size],
            responsive && 'w-full md:w-auto',
            fullWidth && 'w-full',
            className
          )}
          style={style}
          disabled={disabled || isLoading}
          onFocus={onFocus}
          onBlur={onBlur}
          {...props}
        >
          {isLoading && <Spinner size="sm" className="ml-2 text-inherit" />}
          {children}
        </button>
      );
    }

    const variantConfig = variantStyles[variant] || variantStyles.primary;

    // Focus ring color based on variant and theme
    const focusRingColor =
      variant === 'danger' ? '#ef4444' : variant === 'success' ? '#22c55e' : BRAND.secondary;

    const buttonStyle: React.CSSProperties = {
      ...variantConfig.base,
      ...(isHovered && !disabled ? variantConfig.hover : {}),
      ...(isFocused && !disabled
        ? {
            boxShadow: `0 0 0 2px white, 0 0 0 4px ${focusRingColor}`,
          }
        : {}),
      ...style,
    };

    return (
      <button
        ref={ref}
        className={clsx(
          baseClasses,
          variantConfig.className,
          sizeClasses,
          responsiveClasses,
          fullWidth && 'w-full',
          className
        )}
        style={buttonStyle}
        disabled={disabled || isLoading}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      >
        {isLoading && <Spinner size="sm" className="ml-2 text-inherit" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
